import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  MapPin, 
  Star, 
  Calendar,
  ThumbsUp,
  Image as ImageIcon,
  Loader2,
  Download,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  MessageSquare,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  X,
  Sparkles
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SEVERITY_STYLE = {
  tinggi: { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", dot: "bg-red-500" },
  sedang: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", dot: "bg-amber-500" },
  rendah: { bg: "bg-gray-50", border: "border-gray-200", text: "text-gray-600", dot: "bg-gray-400" },
};

const JobDetailsPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  
  // Pagination & filters
  const [reviews, setReviews] = useState([]);
  const [totalReviews, setTotalReviews] = useState(0);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(20);
  const [ratingFilter, setRatingFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");

  // Fetch job metadata (without reviews for speed)
  const fetchJobDetails = async (showRefreshToast = false) => {
    if (showRefreshToast) setRefreshing(true);
    
    try {
      const response = await axios.get(`${API}/scrape-jobs/${jobId}?include_reviews=false`);
      setJob(response.data);
      
      if (showRefreshToast) {
        toast.success("Data berhasil diperbarui");
      }
    } catch (error) {
      console.error("Error fetching job details:", error);
      toast.error("Gagal memuat detail scraping");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Fetch cached AI analysis, if it exists (silent — no toast on 404)
  const fetchAnalysis = async () => {
    try {
      const response = await axios.get(`${API}/scrape-jobs/${jobId}/analysis`);
      setAnalysis(response.data);
    } catch (error) {
      setAnalysis(null);
    }
  };

  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      toast.info("Menganalisa review dengan AI, mohon tunggu...");
      const response = await axios.post(`${API}/scrape-jobs/${jobId}/analyze`);
      setAnalysis(response.data);
      toast.success("Analisa selesai");
    } catch (error) {
      console.error("Error analyzing reviews:", error);
      toast.error(error.response?.data?.detail || "Gagal menganalisa review");
    } finally {
      setAnalyzing(false);
    }
  };

  // Fetch reviews with pagination & filters
  const fetchReviews = useCallback(async () => {
    setReviewsLoading(true);
    try {
      const params = new URLSearchParams({
        limit: pageSize.toString(),
        skip: (page * pageSize).toString(),
      });
      
      if (ratingFilter !== "all") {
        params.append("rating", ratingFilter);
      }
      if (dateFilter !== "all") {
        params.append("date_range", dateFilter);
      }
      if (searchQuery) {
        params.append("search", searchQuery);
      }

      const response = await axios.get(`${API}/scrape-jobs/${jobId}/reviews?${params}`);
      setReviews(response.data.reviews || []);
      setTotalReviews(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching reviews:", error);
      toast.error("Gagal memuat reviews");
    } finally {
      setReviewsLoading(false);
    }
  }, [jobId, page, pageSize, ratingFilter, dateFilter, searchQuery]);

  useEffect(() => {
    fetchJobDetails();
    
    const interval = setInterval(() => {
      if (job && (job.status === 'pending' || job.status === 'processing')) {
        fetchJobDetails();
      }
    }, 3000);
    
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, job?.status]);
  
  useEffect(() => {
    if (job && job.status === 'completed') {
      fetchReviews();
      fetchAnalysis();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job, fetchReviews]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [ratingFilter, dateFilter, searchQuery]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput);
  };

  const clearFilters = () => {
    setRatingFilter("all");
    setDateFilter("all");
    setSearchQuery("");
    setSearchInput("");
    setPage(0);
  };

  const hasFilters = ratingFilter !== "all" || dateFilter !== "all" || searchQuery !== "";
  const totalPages = Math.ceil(totalReviews / pageSize);

  const handleDownloadCSV = async () => {
    if (totalReviews === 0) {
      toast.error("Tidak ada review untuk diunduh");
      return;
    }

    try {
      toast.info("Menyiapkan CSV, mohon tunggu...");
      // Fetch ALL reviews (up to 10000) with same filters for CSV export
      const params = new URLSearchParams({
        limit: "10000",
        skip: "0",
      });
      if (ratingFilter !== "all") params.append("rating", ratingFilter);
      if (dateFilter !== "all") params.append("date_range", dateFilter);
      if (searchQuery) params.append("search", searchQuery);

      const response = await axios.get(`${API}/scrape-jobs/${jobId}/reviews?${params}`);
      const allReviews = response.data.reviews || [];

      const headers = ["Author", "Rating", "Review Text", "Date", "Likes", "Photos Count"];
      const csvRows = [
        headers.join(","),
        ...allReviews.map(review => {
          const author = `"${(review.author_name || '').replace(/"/g, '""')}"`;
          const rating = review.rating || '';
          const text = `"${(review.review_text || '').replace(/"/g, '""').replace(/\n/g, ' ')}"`;
          const date = `"${(review.review_date || '').replace(/"/g, '""')}"`;
          const likes = review.likes_count || 0;
          const photosCount = review.review_photos?.length || 0;
          return [author, rating, text, date, likes, photosCount].join(",");
        })
      ];

      const csvContent = "\uFEFF" + csvRows.join("\n"); // BOM for UTF-8 Excel compatibility
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      
      link.setAttribute("href", url);
      link.setAttribute("download", `reviews_${job.place_name?.replace(/\s+/g, '_') || jobId}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`CSV berhasil diunduh (${allReviews.length} reviews)`);
    } catch (error) {
      console.error("Error downloading CSV:", error);
      toast.error("Gagal mengunduh CSV");
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: "Menunggu", variant: "secondary", icon: Clock },
      processing: { label: "Memproses", variant: "default", icon: Loader2 },
      completed: { label: "Selesai", variant: "success", icon: CheckCircle2 },
      failed: { label: "Gagal", variant: "destructive", icon: XCircle },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className={`w-3 h-3 ${status === 'processing' ? 'animate-spin' : ''}`} />
        {config.label}
      </Badge>
    );
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-1">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            className={`w-4 h-4 ${
              i < rating
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto" />
          <p className="text-gray-600">Memuat detail scraping...</p>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center space-y-4">
            <XCircle className="w-12 h-12 text-red-500 mx-auto" />
            <p className="text-gray-600">Scraping job tidak ditemukan</p>
            <Button onClick={() => navigate('/')}>Kembali ke Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/')}
            data-testid="back-to-home-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900">Detail Scraping</h1>
            <p className="text-gray-500 text-sm mt-1">ID: {jobId}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { fetchJobDetails(true); fetchReviews(); }}
              disabled={refreshing}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            {job.status === 'completed' && totalReviews > 0 && (
              <Button
                size="sm"
                onClick={handleDownloadCSV}
                data-testid="download-csv-btn"
                className="bg-green-600 hover:bg-green-700"
              >
                <Download className="w-4 h-4 mr-2" />
                Download CSV
              </Button>
            )}
            {job.status === 'completed' && totalReviews > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleAnalyze}
                disabled={analyzing}
                data-testid="analyze-ai-btn"
                className="border-purple-300 text-purple-700 hover:bg-purple-50"
              >
                {analyzing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                {analysis ? "Analisa Ulang (AI)" : "Analisa dengan AI"}
              </Button>
            )}
          </div>
        </div>

        {/* Job Info Card */}
        <Card className="border-l-4 border-l-blue-500" data-testid="job-info-card">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="space-y-2 flex-1">
                <div className="flex items-center gap-2">
                  {getStatusBadge(job.status)}
                </div>
                {job.place_name && (
                  <CardTitle className="text-2xl flex items-center gap-2">
                    <MapPin className="w-6 h-6 text-blue-600" />
                    {job.place_name}
                  </CardTitle>
                )}
                {job.place_address && (
                  <CardDescription className="text-base">{job.place_address}</CardDescription>
                )}
              </div>
              {job.place_rating && (
                <div className="text-right">
                  <div className="flex items-center gap-2 justify-end">
                    <Star className="w-6 h-6 fill-yellow-400 text-yellow-400" />
                    <span className="text-3xl font-bold">{job.place_rating}</span>
                  </div>
                  {job.place_total_reviews && (
                    <p className="text-sm text-gray-500 mt-1">
                      {job.place_total_reviews.toLocaleString()} total reviews
                    </p>
                  )}
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span>Dibuat: {new Date(job.created_at).toLocaleString('id-ID')}</span>
              </div>
              {job.completed_at && (
                <div className="flex items-center gap-1">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Selesai: {new Date(job.completed_at).toLocaleString('id-ID')}</span>
                </div>
              )}
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500 mb-1">URL Google Maps:</p>
              <a 
                href={job.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline text-sm break-all"
              >
                {job.url}
              </a>
            </div>

            {job.status === 'failed' && job.error_message && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm font-medium text-red-900">Error:</p>
                <p className="text-sm text-red-600 mt-1">{job.error_message}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Statistics */}
        {job.status === 'completed' && job.total_reviews_scraped > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <CardContent className="pt-6">
                <div className="text-center">
                  <MessageSquare className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-blue-900">{job.total_reviews_scraped}</p>
                  <p className="text-sm text-blue-600">Total Reviews Di-scrape</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
              <CardContent className="pt-6">
                <div className="text-center">
                  <Star className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-yellow-900">{job.place_rating || '-'}</p>
                  <p className="text-sm text-yellow-600">Rating Tempat</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <CardContent className="pt-6">
                <div className="text-center">
                  <Filter className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-purple-900">{totalReviews}</p>
                  <p className="text-sm text-purple-600">Reviews Sesuai Filter</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        {job.status === 'completed' && job.total_reviews_scraped > 0 && (
          <Card data-testid="filters-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Filter className="w-5 h-5" />
                Filter & Search
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                {/* Search */}
                <form onSubmit={handleSearch} className="md:col-span-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      data-testid="review-search-input"
                      type="text"
                      placeholder="Cari dalam review text..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      className="pl-9"
                    />
                    {searchInput && (
                      <button
                        type="button"
                        onClick={() => { setSearchInput(""); setSearchQuery(""); }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        data-testid="clear-search-btn"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </form>

                {/* Rating Filter */}
                <Select value={ratingFilter} onValueChange={setRatingFilter}>
                  <SelectTrigger data-testid="rating-filter-select">
                    <SelectValue placeholder="Filter by Rating" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua Rating</SelectItem>
                    <SelectItem value="5">⭐ 5 Bintang</SelectItem>
                    <SelectItem value="4">⭐ 4 Bintang</SelectItem>
                    <SelectItem value="3">⭐ 3 Bintang</SelectItem>
                    <SelectItem value="2">⭐ 2 Bintang</SelectItem>
                    <SelectItem value="1">⭐ 1 Bintang</SelectItem>
                  </SelectContent>
                </Select>

                {/* Date Filter */}
                <Select value={dateFilter} onValueChange={setDateFilter}>
                  <SelectTrigger data-testid="date-filter-select">
                    <SelectValue placeholder="Filter by Date" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua Waktu</SelectItem>
                    <SelectItem value="week">Minggu Ini</SelectItem>
                    <SelectItem value="month">Bulan Ini</SelectItem>
                    <SelectItem value="year">Tahun Ini</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {hasFilters && (
                <div className="mt-3 flex items-center gap-2">
                  <span className="text-sm text-gray-500">Filter aktif:</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearFilters}
                    data-testid="clear-filters-btn"
                    className="text-xs h-7"
                  >
                    <X className="w-3 h-3 mr-1" />
                    Reset Semua Filter
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* AI Analysis Panel */}
        {job.status === 'completed' && analysis && (
          <Card className="border-l-4 border-l-purple-500" data-testid="ai-analysis-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                Analisa AI
              </CardTitle>
              <CardDescription>
                Dari {analysis.reviews_analyzed} review yang dianalisa &middot; model {analysis.model_used}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Sentiment bar */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Sentimen keseluruhan</p>
                <div className="flex h-3 rounded-full overflow-hidden bg-gray-100">
                  <div className="bg-green-500" style={{ width: `${analysis.sentiment_positive_pct}%` }} title="Positif" />
                  <div className="bg-gray-300" style={{ width: `${analysis.sentiment_neutral_pct}%` }} title="Netral" />
                  <div className="bg-red-500" style={{ width: `${analysis.sentiment_negative_pct}%` }} title="Negatif" />
                </div>
                <div className="flex gap-4 mt-2 text-xs text-gray-500">
                  <span>🟢 Positif {analysis.sentiment_positive_pct}%</span>
                  <span>⚪ Netral {analysis.sentiment_neutral_pct}%</span>
                  <span>🔴 Negatif {analysis.sentiment_negative_pct}%</span>
                </div>
              </div>

              {/* Executive summary */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <p className="text-sm font-medium text-purple-900 mb-1">Ringkasan eksekutif</p>
                <p className="text-sm text-purple-800 leading-relaxed">{analysis.executive_summary}</p>
              </div>

              {/* Category breakdown */}
              {analysis.categories?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Kategori masalah (dari yang paling sering)</p>
                  <div className="space-y-2">
                    {analysis.categories.map((cat, idx) => {
                      const style = SEVERITY_STYLE[cat.severity] || SEVERITY_STYLE.rendah;
                      return (
                        <div key={idx} className={`flex items-start gap-3 p-3 rounded-lg border ${style.bg} ${style.border}`}>
                          <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${style.dot}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`font-medium text-sm ${style.text}`}>{cat.name}</span>
                              <Badge variant="outline" className="text-xs">{cat.count} review</Badge>
                              <Badge variant="outline" className={`text-xs capitalize ${style.text}`}>{cat.severity}</Badge>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">{cat.summary}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {job.status === 'completed' && !analysis && totalReviews > 0 && (
          <Card className="border-dashed" data-testid="ai-analysis-empty-card">
            <CardContent className="py-8 text-center space-y-2">
              <Sparkles className="w-10 h-10 mx-auto text-purple-300" />
              <p className="text-gray-500">Belum ada analisa AI untuk toko ini</p>
              <Button variant="outline" size="sm" onClick={handleAnalyze} disabled={analyzing}>
                {analyzing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                Analisa Sekarang
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Reviews List */}
        <Card data-testid="reviews-list-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Reviews ({totalReviews}{hasFilters && job.total_reviews_scraped ? ` / ${job.total_reviews_scraped}` : ''})
                </CardTitle>
                <CardDescription>
                  {job.status === 'processing' ? 'Sedang mengambil reviews...' : 
                   job.status === 'completed' ? `Menampilkan halaman ${page + 1} dari ${totalPages || 1}` :
                   job.status === 'pending' ? 'Menunggu untuk diproses...' :
                   'Scraping gagal'}
                </CardDescription>
              </div>
              {reviewsLoading && (
                <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
              )}
            </div>
          </CardHeader>
          <CardContent>
            {job.status === 'processing' || job.status === 'pending' ? (
              <div className="flex flex-col items-center justify-center py-12 space-y-4">
                <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
                <p className="text-gray-600">Mohon tunggu, sedang mengambil reviews...</p>
              </div>
            ) : reviews.length === 0 && !reviewsLoading ? (
              <div className="text-center py-12 space-y-2">
                <MessageSquare className="w-16 h-16 mx-auto text-gray-300" />
                <p className="text-gray-500">
                  {hasFilters ? "Tidak ada review yang cocok dengan filter" : "Tidak ada review yang ditemukan"}
                </p>
                {hasFilters && (
                  <Button variant="outline" size="sm" onClick={clearFilters}>
                    Reset Filter
                  </Button>
                )}
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  {reviews.map((review, index) => (
                    <div key={review.id || index}>
                      <div className="space-y-3" data-testid={`review-card-${index}`}>
                        <div className="flex items-start gap-3">
                          <Avatar className="w-10 h-10">
                            <AvatarImage src={review.author_photo_url} alt={review.author_name} />
                            <AvatarFallback>
                              {review.author_name?.charAt(0)?.toUpperCase() || 'U'}
                            </AvatarFallback>
                          </Avatar>
                          
                          <div className="flex-1 space-y-2">
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="font-semibold text-gray-900">{review.author_name}</p>
                                {review.review_date && (
                                  <p className="text-xs text-gray-500">{review.review_date}</p>
                                )}
                              </div>
                              {review.rating && (
                                <div className="flex items-center gap-2">
                                  {renderStars(review.rating)}
                                  <span className="text-sm font-medium">{review.rating}</span>
                                </div>
                              )}
                            </div>
                            
                            {review.review_text && (
                              <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">
                                {review.review_text}
                              </p>
                            )}
                            
                            {review.review_photos && review.review_photos.length > 0 && (
                              <div className="flex gap-2 flex-wrap">
                                {review.review_photos.map((photo, photoIndex) => (
                                  <img
                                    key={photoIndex}
                                    src={photo}
                                    alt={`Review photo ${photoIndex + 1}`}
                                    className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                                  />
                                ))}
                              </div>
                            )}
                            
                            {review.likes_count > 0 && (
                              <div className="flex items-center gap-1 text-gray-500 text-sm">
                                <ThumbsUp className="w-3 h-3" />
                                <span>{review.likes_count}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      {index < reviews.length - 1 && <Separator className="my-4" />}
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-6 flex items-center justify-between pt-4 border-t" data-testid="pagination-controls">
                    <div className="text-sm text-gray-600">
                      Menampilkan {page * pageSize + 1} - {Math.min((page + 1) * pageSize, totalReviews)} dari {totalReviews}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(0)}
                        disabled={page === 0 || reviewsLoading}
                        data-testid="pagination-first-btn"
                      >
                        First
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.max(0, p - 1))}
                        disabled={page === 0 || reviewsLoading}
                        data-testid="pagination-prev-btn"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        Prev
                      </Button>
                      <span className="text-sm px-3 py-1 bg-gray-100 rounded" data-testid="pagination-info">
                        {page + 1} / {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                        disabled={page >= totalPages - 1 || reviewsLoading}
                        data-testid="pagination-next-btn"
                      >
                        Next
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(totalPages - 1)}
                        disabled={page >= totalPages - 1 || reviewsLoading}
                        data-testid="pagination-last-btn"
                      >
                        Last
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default JobDetailsPage;
